import React, { useState } from "react";
import { Pencil, Trash2, RefreshCw, ExternalLink, Square } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "../ui/Button";
import { Skeleton } from "../ui/Skeleton";
import { Tooltip, TooltipProvider } from "../ui/Tooltip";
import { Pagination } from "../ui/Pagination";
import { ReleaseEditDialog } from "./ReleaseEditDialog";
import { ReleaseDeleteDialog } from "./ReleaseDeleteDialog";
import { useAdminReleases } from "../../hooks/useAdminApi";
import { useGenerationStream } from "../../hooks/useAdminStream";
import { formatDate } from "../../utils";
import type { Release } from "../../types";

interface ReleaseTableProps {
  page: number;
  setPage: (n: number) => void;
  limit: number;
  setLimit: (n: number) => void;
  search: string;
}

export const ReleaseTable: React.FC<ReleaseTableProps> = ({
  page,
  setPage,
  limit,
  setLimit,
  search,
}) => {
  const { data, isLoading } = useAdminReleases(page, limit, search);
  const stream = useGenerationStream();

  const PHASE_LABEL: Record<string, string> = {
    commits: "Buscando commits",
    ai: "Gerando com IA",
    saving: "Salvando",
  };

  const [editing, setEditing] = useState<Release | null>(null);
  const [deleting, setDeleting] = useState<Release | null>(null);

  const releases = data?.data || [];
  const meta = data?.meta;

  if (isLoading) {
    return (
      <div className="space-y-2">
        {[...Array(6)].map((_, i) => (
          <Skeleton key={i} className="h-14 w-full" />
        ))}
      </div>
    );
  }

  if (releases.length === 0) {
    return (
      <div className="border border-border rounded-md bg-card py-12 text-center">
        <p className="text-sm text-muted-foreground">
          {search ? "Nenhuma release encontrada com esse termo." : "Nenhuma release cadastrada ainda."}
        </p>
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className="border border-border rounded-md bg-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 border-b border-border">
              <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="px-4 py-2.5 font-medium">Tag</th>
                <th className="px-4 py-2.5 font-medium">Publicada</th>
                <th className="px-4 py-2.5 font-medium hidden md:table-cell">Provedor</th>
                <th className="px-4 py-2.5 font-medium text-right">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {releases.map((release) => (
                <tr key={release.id} className="hover:bg-muted/30 transition-colors">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2 min-w-0">
                      <Link
                        to={`/changelog/${encodeURIComponent(release.tagName)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-mono text-sm font-semibold text-foreground hover:text-primary transition-colors truncate"
                      >
                        {release.tagName}
                      </Link>
                      <Link
                        to={`/changelog/${encodeURIComponent(release.tagName)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-muted-foreground hover:text-foreground transition-colors"
                        aria-label="Abrir página pública"
                      >
                        <ExternalLink className="w-3.5 h-3.5" aria-hidden="true" />
                      </Link>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">
                    <time dateTime={release.releaseDate} className="tabular-nums">
                      {formatDate(release.releaseDate)}
                    </time>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground hidden md:table-cell capitalize">
                    {release.aiProvider || "-"}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      {stream.isRunning && stream.tag === release.tagName ? (
                        <>
                          <span className="text-xs text-muted-foreground whitespace-nowrap">
                            {stream.phase
                              ? PHASE_LABEL[stream.phase]
                              : "Iniciando…"}
                          </span>
                          <Tooltip content="Cancelar geração">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={stream.abort}
                              aria-label="Cancelar geração"
                            >
                              <Square
                                className="w-3.5 h-3.5"
                                aria-hidden="true"
                              />
                            </Button>
                          </Tooltip>
                        </>
                      ) : (
                        <Tooltip content="Regenerar pela IA">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() =>
                              stream.start(
                                `?tag=${encodeURIComponent(release.tagName)}&regenerate=1`
                              )
                            }
                            disabled={stream.isRunning}
                            aria-label="Regenerar release"
                          >
                            <RefreshCw
                              className="w-3.5 h-3.5"
                              aria-hidden="true"
                            />
                          </Button>
                        </Tooltip>
                      )}
                      <Tooltip content="Editar conteúdo">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => setEditing(release)}
                          aria-label="Editar release"
                        >
                          <Pencil className="w-3.5 h-3.5" aria-hidden="true" />
                        </Button>
                      </Tooltip>
                      <Tooltip content="Remover">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-muted-foreground hover:text-destructive"
                          onClick={() => setDeleting(release)}
                          aria-label="Remover release"
                        >
                          <Trash2 className="w-3.5 h-3.5" aria-hidden="true" />
                        </Button>
                      </Tooltip>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {meta && meta.totalPages > 1 && (
        <div className="mt-6">
          <Pagination
            currentPage={meta.page}
            totalPages={meta.totalPages}
            itemsPerPage={limit}
            onPageChange={setPage}
            onItemsPerPageChange={(n) => {
              setLimit(n);
              setPage(1);
            }}
          />
        </div>
      )}

      <ReleaseEditDialog
        release={editing}
        open={!!editing}
        onOpenChange={(o) => !o && setEditing(null)}
      />
      <ReleaseDeleteDialog
        release={deleting}
        open={!!deleting}
        onOpenChange={(o) => !o && setDeleting(null)}
      />
    </TooltipProvider>
  );
};
